import {useEffect, useMemo, useState} from 'react';
import Button from '@/shared/components/Button';
import ActionsMenu from '@/shared/components/Table/ActionMenu';
import {BODY1, defaultPage, defaultPageSize, SUBTITLE1, TEXT} from '@/utils/constants';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  TableSortLabel,
} from '@mui/material';
import {ReactComponent as ImportIcon} from '@icons/import.svg';
import {ReactComponent as RowHorizontal} from '@icons/row-horizontal.svg';
import {ReactComponent as SearchIcon} from '@icons/search-normal.svg';
import {ReactComponent as UpDownIcon} from '@/assets/icons/arrow-up-down.svg';
import Input from '@/shared/components/Inputs/Input';
import {ReportsTableStyle} from './ReportsTable.style';
import Modal from '@/shared/components/Modal';
import {columnsMenuStyle} from '@/shared/components/Table/ColumnsMenu/ColumnsMenu.style';
import FiltersModal from '../FiltersModal';
import Pagination from '@/shared/components/Pagination';
import EmptyTableData from '@/shared/components/Table/EmptyTableData';
import {useSelectedSection} from '@/contexts/SelectedSectionContext';
import {OrganizationReportsService} from '@/utils/services/organization-reports.service';
import {getErrorMessage, downloadFile} from '@/utils/helpers';
import {toast} from 'react-toastify';
import {getCurrentEventSelector} from '@/store/organization-event/selector';
import {useAppDispatch, useAppSelector} from '@/store/hooks';
import {getFinancialReportSelector, getReportColumnsSelector} from '@/store/organization-report/selectors';
import {getReportedColumns, getReportFullData} from '@/store/organization-report/actions';
import {OrganizationStripeService} from '@/utils/services/organization-stripe.services';
import {PaymentResult} from '@/utils/types';
import {REPORTS_GET_FILE} from '@/utils/enums/organization-reports.enum';
import {orgStorage} from '@/utils/services';
import {STORAGE_KEY} from '@/utils/enums';
import {CommunicationService} from '@/utils/services/organization-communication.services';

const ReportsTable = () => {
  const dispatch = useAppDispatch();

  const currentEvent = useAppSelector(getCurrentEventSelector);
  const columns = useAppSelector(getReportColumnsSelector);
  const fullReportData = useAppSelector(getFinancialReportSelector);

  const columnOptions = columns.map((col) => col.label);
  const columnKeyMap = useMemo(
    () => columns.reduce((acc, col) => ({...acc, [col.label]: col.key}), {} as Record<string, string>),
    [columns]
  );

  const {selectedSections} = useSelectedSection();

  const [selectedColumns, setSelectedColumns] = useState<Record<string, boolean>>({});
  const [isColumnSelectOpened, setIsColumnSelectOpened] = useState(false);
  const [searchName, setSearchName] = useState('');
  const [sortColumn, setSortColumn] = useState<string>('');
  const [isOrderDesc, setIsOrderDesc] = useState(false);
  const [page, setPage] = useState<number>(defaultPage);

  const buildDefaultColumnsMap = (): Record<string, boolean> =>
    columns.reduce((acc, col) => ({...acc, [col.label]: col.defaultVisible}), {} as Record<string, boolean>);

  const sendPaymentLink = async (attendeeId: string, paymentStatus: string) => {
    if (!currentEvent?.id || paymentStatus === null || paymentStatus === PaymentResult.COMPLETED) return;

    try {
      await OrganizationStripeService.sendPaymentLink(+attendeeId, currentEvent?.id);
      toast.success('Payment link sent successfully');
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const loadPersistedColumnSelection = (): Record<string, boolean> | null => {
    const raw = orgStorage.get<string>(STORAGE_KEY.REPORTS_TABLE_SELECTED_COLUMNS);
    if (!raw || typeof raw !== 'string') return null;
    try {
      const parsed = JSON.parse(raw) as Record<string, boolean>;
      return columnOptions.reduce(
        (acc, col) => ({...acc, [col]: typeof parsed[col] === 'boolean' ? parsed[col] : false}),
        {} as Record<string, boolean>
      );
    } catch {
      return null;
    }
  };

  const persistColumnSelection = (cols: Record<string, boolean>) => {
    const payload = columnOptions.reduce((acc, col) => ({...acc, [col]: !!cols[col]}), {} as Record<string, boolean>);
    orgStorage.set(STORAGE_KEY.REPORTS_TABLE_SELECTED_COLUMNS, JSON.stringify(payload));
  };

  const sendReceiptEmail = async (attendeeId: string, paymentStatus: string) => {
    if (!attendeeId || paymentStatus !== PaymentResult.COMPLETED) return;
    try {
      await CommunicationService.emailAttendeeReceipt(+attendeeId);
      toast.success('Receipt sent successfully');
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const handleConfirmColumns = (cols: Record<string, boolean>) => {
    setSelectedColumns(cols);
    persistColumnSelection(cols);
    setPage(1);
  };

  useEffect(() => {
    if (!columnOptions.length) return;

    setSelectedColumns((prev) => {
      const hasPrev = Object.keys(prev).length > 0;
      const base = hasPrev ? prev : loadPersistedColumnSelection() ?? buildDefaultColumnsMap();

      const next = selectedSections.reduce(
        (acc, section) => (columnOptions.includes(section) ? {...acc, [section]: true} : acc),
        base
      );

      if (next !== base) persistColumnSelection(next);
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columnOptions.length, selectedSections]);

  const getColumns = async () => {
    try {
      await dispatch(getReportedColumns()).unwrap();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const handleDownloadCsv = async () => {
    if (!currentEvent?.id) return;

    const selectedLabels = Object.keys(selectedColumns).filter((col) => selectedColumns[col]);
    const data = {
      page: 1,
      pageSize: defaultPageSize,
      isOrderDesc,
      eventId: currentEvent?.id,
      columns: selectedLabels.map((label) => columnKeyMap[label] ?? label),
      ...(searchName && {searchName}),
    };
    try {
      const file = await OrganizationReportsService.getFullReportCsv(data);
      downloadFile(file.data, REPORTS_GET_FILE.FULL_REPORT);
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const getReportTableData = async () => {
    if (!currentEvent?.id) return;
    if (!Object.keys(selectedColumns).length) return;

    const selectedLabels = Object.keys(selectedColumns).filter((col) => selectedColumns[col]);
    const data = {
      page,
      pageSize: defaultPageSize,
      isOrderDesc,
      eventId: currentEvent?.id,
      columns: selectedLabels.map((label) => columnKeyMap[label] ?? label),
      ...(searchName && {searchName}),
      sortColumn: sortColumn ?? '',
    };
    try {
      await dispatch(getReportFullData(data)).unwrap();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const handleSearchChange = (name: string) => {
    setSearchName(name);
    setPage(1);
  };

  const handleSortChange = (newSortColumn: string) => () => {
    setSortColumn(newSortColumn);
    const isAsc = sortColumn !== newSortColumn || !isOrderDesc;

    setIsOrderDesc(isAsc);
    setPage(1);
  };

  const handlePaginationChange = (step: number) => {
    setPage(step);
  };

  const disabled = !fullReportData?.data.length || !Object.keys(selectedColumns).length;

  useEffect(() => {
    getColumns();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    getReportTableData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentEvent?.id, page, searchName, selectedColumns, sortColumn, isOrderDesc]);

  return (
    <Box sx={ReportsTableStyle.wrapper} id="reports-table">
      <Box sx={ReportsTableStyle.filterRow}>
        <Typography variant={SUBTITLE1} sx={ReportsTableStyle.title} />
        <Box sx={columnsMenuStyle.buttonWrap}>
          <Button
            startIcon={<RowHorizontal />}
            onClick={() => setIsColumnSelectOpened(true)}
            disableRipple
            variant={TEXT}
            title="Columns"
            sx={{
              ...(disabled
                ? columnsMenuStyle.disabledButton
                : isColumnSelectOpened
                ? columnsMenuStyle.activeButton
                : columnsMenuStyle.button),
            }}
            disabled={disabled}
          />
        </Box>
        <Box sx={ReportsTableStyle.search}>
          <SearchIcon />
          <Input placeholder="Search" value={searchName} onChange={handleSearchChange} />
        </Box>
        <Box>
          <Button
            sx={ReportsTableStyle.download}
            startIcon={<ImportIcon />}
            onClick={handleDownloadCsv}
            title="Download.CSV"
            disabled={disabled}
          />
        </Box>
      </Box>

      {fullReportData?.data.length ? (
        <Box sx={{display: 'flex', flexDirection: 'column', flex: 1}}>
          <TableContainer sx={ReportsTableStyle.tableContainer}>
            <Table sx={ReportsTableStyle.tableStyle} aria-label="simple table">
              <TableHead>
                <TableRow sx={{height: '48px'}}>
                  {columnOptions
                    .filter((col) => selectedColumns[col])
                    .map((col) => (
                      <TableCell className={`${col}`} sx={{minWidth: col === 'Phone' ? '200px' : '100px'}} key={col}>
                        <TableSortLabel active onClick={handleSortChange(columnKeyMap[col])} IconComponent={UpDownIcon}>
                          {col}
                        </TableSortLabel>
                      </TableCell>
                    ))}
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {fullReportData?.data.map((row, index) => (
                  <TableRow key={index}>
                    {columnOptions
                      .filter((col) => selectedColumns[col])
                      .map((col) => {
                        const rowKey = columnKeyMap[col] ?? col;
                        const value = (row as Record<string, any>)[rowKey];
                        const isPaymentCol =
                          col === 'Total payments' || col === 'Table payments' || col === 'Auction payments';

                        return (
                          <TableCell key={col}>
                            <Typography
                              sx={{
                                color: isPaymentCol
                                  ? value < 0
                                    ? 'var(--error-700) !important'
                                    : 'var(--success-700) !important'
                                  : '',
                              }}
                            >
                              {(() => {
                                if (typeof value === 'number') {
                                  if (col === 'Bidder number (#)') return value;
                                  return value >= 0 ? `$${value}` : `-$${Math.abs(value)}`;
                                }
                                return value || '-';
                              })()}
                            </Typography>
                          </TableCell>
                        );
                      })}
                    <TableCell>
                      <ActionsMenu
                        content={
                          <Box sx={ReportsTableStyle.menu}>
                            <Typography
                              sx={{
                                ...ReportsTableStyle.menuContent,
                                ...((row.paymentStatus === PaymentResult.COMPLETED || !row.paymentStatus) &&
                                  ReportsTableStyle.disabledMenuItem),
                              }}
                              onClick={() => {
                                sendPaymentLink(row.bidderId, row.paymentStatus);
                              }}
                              variant={BODY1}
                            >
                              Send payment link
                            </Typography>
                            <Typography
                              sx={{
                                ...ReportsTableStyle.menuContent,
                                ...((!row.paymentStatus || row.paymentStatus !== PaymentResult.COMPLETED) && ReportsTableStyle.disabledMenuItem),
                              }}
                              onClick={() => {
                                sendReceiptEmail(row.bidderId, row.paymentStatus);
                              }}
                              variant={BODY1}
                            >
                              Send receipt email
                            </Typography>
                          </Box>
                        }
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Box sx={ReportsTableStyle.pageinationContainer}>
            <Pagination
              page={page}
              pageSize={defaultPageSize}
              total={fullReportData?.totalCount || 0}
              onChange={(step) => handlePaginationChange(step)}
              displayMode="range"
            />
          </Box>
        </Box>
      ) : (
        <EmptyTableData />
      )}

      <Modal
        onClose={() => setIsColumnSelectOpened(false)}
        open={isColumnSelectOpened}
        title="Change Columns"
        parentClassName={{width: '100%'}}
        sx={ReportsTableStyle.modalWrapper}
      >
        <FiltersModal
          selectedColumns={selectedColumns}
          onConfirm={handleConfirmColumns}
          onClose={() => setIsColumnSelectOpened(false)}
        />
      </Modal>
    </Box>
  );
};

export default ReportsTable;
